import { Injectable } from '@angular/core';
import { CampaignInfo } from '../../models/db-models';

const STORAGE_KEY_FIRST_TOUCH = 'bpa.campaign';
const STORAGE_KEY_LAST_TOUCH = 'bpa.campaign.lastTouch';

@Injectable({
  providedIn: 'root'
})
export class CampaignTrackingService {

  static captureFromUrl(href: string): void {
    try {
      const info = CampaignTrackingService.parse(href);
      if (!CampaignTrackingService.hasTrackingData(info)) {
        return;
      }

      const storage = CampaignTrackingService.getStorage();
      if (!storage) {
        return;
      }

      const serialized = JSON.stringify(info);

      if (!storage.getItem(STORAGE_KEY_FIRST_TOUCH)) {
        storage.setItem(STORAGE_KEY_FIRST_TOUCH, serialized);
      }

      storage.setItem(STORAGE_KEY_LAST_TOUCH, serialized);
    } catch (error) {
      console.warn('CampaignTrackingService.captureFromUrl failed', error);
    }
  }

  private static parse(href: string): CampaignInfo {
    const url = new URL(href);
    const params = url.searchParams;

    const referrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined;

    const info: CampaignInfo = {
      utmSource: params.get('utm_source') ?? undefined,
      utmMedium: params.get('utm_medium') ?? undefined,
      utmCampaign: params.get('utm_campaign') ?? undefined,
      utmTerm: params.get('utm_term') ?? undefined,
      utmContent: params.get('utm_content') ?? undefined,
      gclid: params.get('gclid') ?? undefined,
      gadCampaignId: params.get('gad_campaignid') ?? undefined,
      gadAdGroupId: params.get('gad_adgroupid') ?? undefined,
      gadNetwork: params.get('gad_network') ?? undefined,
      gadDevice: params.get('gad_device') ?? undefined,
      fbclid: params.get('fbclid') ?? undefined,
      landingUrl: url.toString(),
      referrer,
      capturedAt: Date.now(),
    };

    return CampaignTrackingService.stripUndefined(info);
  }

  private static hasTrackingData(info: CampaignInfo): boolean {
    return !!(
      info.utmSource ||
      info.utmMedium ||
      info.utmCampaign ||
      info.utmTerm ||
      info.utmContent ||
      info.gclid ||
      info.gadCampaignId ||
      info.gadAdGroupId ||
      info.gadNetwork ||
      info.gadDevice ||
      info.fbclid
    );
  }

  private static stripUndefined(info: CampaignInfo): CampaignInfo {
    const cleaned: CampaignInfo = {};
    (Object.keys(info) as (keyof CampaignInfo)[]).forEach((key) => {
      const value = info[key];
      if (value !== undefined && value !== null && value !== '') {
        (cleaned as Record<string, unknown>)[key] = value;
      }
    });
    return cleaned;
  }

  private static getStorage(): Storage | undefined {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return undefined;
      }
      return window.localStorage;
    } catch {
      return undefined;
    }
  }

  getStoredCampaign(): CampaignInfo | undefined {
    const storage = CampaignTrackingService.getStorage();
    if (!storage) {
      return undefined;
    }

    const raw = storage.getItem(STORAGE_KEY_FIRST_TOUCH);
    if (!raw) {
      return undefined;
    }

    try {
      return JSON.parse(raw) as CampaignInfo;
    } catch {
      return undefined;
    }
  }

  clear(): void {
    const storage = CampaignTrackingService.getStorage();
    if (!storage) {
      return;
    }
    storage.removeItem(STORAGE_KEY_FIRST_TOUCH);
    storage.removeItem(STORAGE_KEY_LAST_TOUCH);
  }
}
